import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getEndpoint } from 'src/app/core/services/functions.service';
import { Tienda } from '../models/tienda-cita-futura.model';
import { PeriodoMetas } from '../models/periodo-metas.model';

export interface ComsTiendaCitaFutura {
  id?: number;
  c_emp: string;
  c_age: string;
  periodo: string;  // formato YYYYMM
  usuario_creacion: string;
  fecha_creacion?: Date;
  usuario_modificacion?: string;
  fecha_modificacion?: Date;
  estado: string;
}

interface ComsTiendaCitaFuturaResponse {
  id?: number;
  c_emp: string;
  c_age: string;
  periodo: string | Date;
  usuario_creacion: string;
  fecha_creacion?: string | Date;
  usuario_modificacion?: string;
  fecha_modificacion?: string | Date;
  estado: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

@Injectable({
  providedIn: 'root'
})
export class ComsTiendaCitaFuturaService {
  //private apiUrl = 'http://127.0.0.1:8000/coms-tienda-cita-futura';
  ENDPOINT = getEndpoint('coms_tienda_cita_futura');
  private tiendas: Tienda[] = [];
  constructor(private http: HttpClient) { }

  private transformDates(data: ComsTiendaCitaFuturaResponse): ComsTiendaCitaFutura {
    let periodo: string;

    // Transformar periodo
    if (data.periodo instanceof Date) {
      const date = new Date(data.periodo);
      periodo = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
    } else if (typeof data.periodo === 'string') {
      if (data.periodo.includes('-')) {
        const [año, mes] = data.periodo.split('-');
        periodo = `${año}${mes}`;
      } else {
        periodo = data.periodo;
      }
    } else {
      periodo = '000000';
    }

    // Transformar fechas a objetos Date
    const fecha_creacion = data.fecha_creacion ? new Date(data.fecha_creacion) : undefined;
    const fecha_modificacion = data.fecha_modificacion ? new Date(data.fecha_modificacion) : undefined;

    return {
      ...data,
      periodo,
      fecha_creacion,
      fecha_modificacion
    };
  }

  getAll(company: string): Observable<ComsTiendaCitaFutura[]> {
    // Agregar timestamp para evitar el caché
    const timestamp = new Date().getTime();
    return this.http.get<ComsTiendaCitaFuturaResponse[]>(`${this.ENDPOINT}?t=${timestamp}`).pipe(
      map(data => data.map(item => this.transformDates(item)))
    );
  }

  getById(company: string,id: number): Observable<ComsTiendaCitaFutura> {
    return this.http.get<ComsTiendaCitaFuturaResponse>(`${this.ENDPOINT}/${id}`).pipe(
      map(data => this.transformDates(data))
    );
  }

  create(company: string,data: Omit<ComsTiendaCitaFutura, 'id' | 'fecha_creacion' | 'fecha_modificacion'>): Observable<ComsTiendaCitaFutura> {
    return this.http.post<ComsTiendaCitaFuturaResponse>(`${this.ENDPOINT}`, data,
      {
        headers: { 'company': company }
      }
    ).pipe(
      map(response => this.transformDates(response))
    );
  }

  update(company: string,id: number, data: Partial<ComsTiendaCitaFutura>): Observable<ComsTiendaCitaFutura> {
    const dataToSend = { ...data };
    
    // Si hay periodo, convertirlo a formato ISO
    if (dataToSend.periodo) {
      const año = dataToSend.periodo.substring(0, 4);
      const mes = dataToSend.periodo.substring(4, 6);
      dataToSend.periodo = `${año}-${mes}-01T00:00:00.000Z`;
    }

    return this.http.put<ComsTiendaCitaFuturaResponse>(`${this.ENDPOINT}/${id}`, dataToSend).pipe(
      map(response => this.transformDates(response))
    );
  }

  deleteTiendaCitaFutura(company: string,id: number): Observable<void> {
    return this.http.delete<void>(`${this.ENDPOINT}/${id}`,
      {
        headers: { 'company': company }
      }
    );
  }

  getCitasFuturasFiltradas(company: string,filtro: string): Observable<ComsTiendaCitaFutura[]> {
    return this.getAll(company).pipe(
      map(citas => 
        citas.filter(cita => 
          cita.c_emp.toLowerCase().includes(filtro.toLowerCase()) ||
          cita.c_age.toLowerCase().includes(filtro.toLowerCase()) ||
          cita.periodo.includes(filtro)
        )
      )
    );
  }

  getPaginated(company: string,page: number, pageSize: number, periodo?: string): Observable<PaginatedResponse<ComsTiendaCitaFutura>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());
    
    if (periodo) {
      params = params.append('filter_periodo', periodo);
    }

    return this.http.get<any>(`${this.ENDPOINT}`, { params ,
      headers: { 'company': company }
    })
      .pipe(
        map(response => {
          // Si la respuesta es un array, lo convertimos al formato paginado
          if (Array.isArray(response)) {
            // Si hay periodo, filtramos el array
            let items = response;
            if (periodo) {
              items = response.filter(item => item.periodo === periodo);
            }
            
            return {
              items: items.map(item => this.transformDates(item)),
              total: items.length,
              page: page,
              page_size: pageSize
            };
          }
          // Si ya viene en formato paginado
          return {
            ...response,
            items: response.items ? response.items.map(item => this.transformDates(item)) : []
          };
        })
      );
  }


//Endponis de tiendas para la vista
  async getAllTiendas(company: string): Promise<Tienda[]> {
    try {
      const headers = new HttpHeaders().set('company', company);
      const response = await this.http.get<Tienda[]>(`${this.ENDPOINT}/tiendas`, {
        headers: headers
      }).toPromise();
      return response || [];
    } catch (error) {  
      return [];
    }
  }

  async getTiendasByDepartamento(company: string, departamentos: string[]): Promise<Tienda[]> {
    const allTiendas = await this.getAllTiendas(company);
    const tiendasFiltradas = allTiendas.filter(tienda => 
      departamentos.includes(tienda.departamento || tienda.dep || '')
    );
    return Promise.resolve(tiendasFiltradas);
  }

  async getTiendasByCiudad(company: string, ciudades: string[]): Promise<Tienda[]> {
    const allTiendas = await this.getAllTiendas(company);
    const tiendasFiltradas = allTiendas.filter(tienda => 
      ciudades.includes(tienda.ciudad || tienda.ciu || '')
    );
    return Promise.resolve(tiendasFiltradas);
  }

  async getDepartamentosByCiudades(company: string, ciudades: string[]): Promise<{ codigo: string, nombre_departamento: string }[]> {
    const allTiendas = await this.getAllTiendas(company);
    const departamentosSet = new Set<string>();
    const departamentos: { codigo: string, nombre_departamento: string }[] = [];

    allTiendas
      .filter(tienda => ciudades.includes(tienda.ciudad || tienda.ciu || ''))
      .forEach(tienda => {
        const depCodigo = tienda.departamento || tienda.dep || '';
        const depNombre = tienda.nombre_departamento || depCodigo;
        
        if (!departamentosSet.has(depCodigo)) {
          departamentosSet.add(depCodigo);
          departamentos.push({
            codigo: depCodigo,
            nombre_departamento: depNombre
          });
        }
      });
    return Promise.resolve(departamentos);
  }

  async getCiudadesByDepartamentos(company: string, departamentos: string[]): Promise<{ codigo: string, nombre_municipio: string }[]> {
    const allTiendas = await this.getAllTiendas(company);
    const ciudadesSet = new Set<string>();
    const ciudades: { codigo: string, nombre_municipio: string }[] = [];

    allTiendas
      .filter(tienda => departamentos.includes(tienda.departamento || tienda.dep || ''))
      .forEach(tienda => {
        const ciudadCodigo = tienda.ciudad || tienda.ciu || '';
        const ciudadNombre = tienda.nombre_municipio || ciudadCodigo;
        
        if (!ciudadesSet.has(ciudadCodigo)) {
          ciudadesSet.add(ciudadCodigo);
          ciudades.push({
            codigo: ciudadCodigo,
            nombre_municipio: ciudadNombre
          });
        }
      });
    return Promise.resolve(ciudades);
  }

  async getTiendasFiltradas(company: string, ciudades: string[] = [], departamentos: string[] = [], searchTerm: string = ''): Promise<Tienda[]> {
    const allTiendas = await this.getAllTiendas(company);
    let tiendasFiltradas = [...allTiendas];

    if (ciudades.length > 0) {
      tiendasFiltradas = tiendasFiltradas.filter(t => 
        ciudades.includes(t.ciudad || t.ciu || '')
      );
    }

    if (departamentos.length > 0) {
      tiendasFiltradas = tiendasFiltradas.filter(t => 
        departamentos.includes(t.departamento || t.dep || '')
      );
    }

    if (searchTerm) {
      const termLower = searchTerm.toLowerCase();
      tiendasFiltradas = tiendasFiltradas.filter(t => 
        (t.nom || '').toLowerCase().includes(termLower) || 
        (t.c_age || '').toLowerCase().includes(termLower)
      );
    }
    return Promise.resolve(tiendasFiltradas);
  }

  delete(id: number, usuario_modificacion: string, c_emp: string, company: string): Observable<any> {
    const params = new HttpParams()
      .set('usuario_modificacion', usuario_modificacion)
      .set('c_emp', c_emp);
    
    return this.http.delete(`${this.ENDPOINT}/${id}`, { params ,
      headers: { 'company': company }
    } );
  }
}
